import { type ApiArchive, EnumArchiveRunState, type IId } from '@Model';
import { Badge, type BadgeVariations, type Column } from '@synqo/frontend-component-library';
import { formatFileSize } from '@utils/formatFileSize';

/** An archive as a table row, which the table keys by `id`. */
export type ArchiveRow = ApiArchive & IId;

/** The badge of an archive: being created, ready, expired or failed. */
// @ai-generated 2026-10-01T05:46:19+00:00 by philipp.kammerer@synqony.com using Claude Opus 5.5 (1M context)
export function describeArchiveState(archive: ApiArchive): {
    label: string;
    variant: Extract<BadgeVariations, 'success' | 'danger' | 'info' | 'default'>;
} {
    switch (archive.state) {
        case EnumArchiveRunState.Scheduled:
        case EnumArchiveRunState.Running:
            return { label: 'Wird erstellt', variant: 'info' };
        case EnumArchiveRunState.Failed:
            return { label: 'Fehlgeschlagen', variant: 'danger' };
        case EnumArchiveRunState.Finished:
            return archive.is_expired
                ? { label: 'Abgelaufen', variant: 'default' }
                : { label: 'Bereit', variant: 'success' };
    }
}

const isFinished = (archive: ApiArchive) => archive.state === EnumArchiveRunState.Finished;

export const archiveColumns: Column<ArchiveRow>[] = [
    {
        name: 'state',
        label: 'Status',
        renderCell: ({ rowData }) => {
            const { label, variant } = describeArchiveState(rowData);
            return <Badge variant={variant}>{label}</Badge>;
        },
    },
    { name: 'file_name', label: 'Datei' },
    { name: 'created_at', label: 'Erstellt am', type: 'datetime' },
    {
        name: 'document_count',
        label: 'Dokumente',
        renderCell: ({ rowData }) => (isFinished(rowData) ? String(rowData.document_count) : '-'),
    },
    {
        name: 'file_size',
        label: 'Größe',
        renderCell: ({ rowData }) => (isFinished(rowData) ? formatFileSize(rowData.file_size) : '-'),
    },
    { name: 'expires_at', label: 'Verfügbar bis', type: 'datetime' },
];
